import { useNavigate, useParams } from "@solidjs/router";
import { type Project, type ProjectId, ROLE_OWNER, type TenantSummary } from "@storytell/types";
import { useI18n } from "~/i18n";
import { orgDefaultProject } from "~/lib/orgDefaultProject";
import { ownedOrgTenant } from "~/lib/ownedOrgTenant";
import { useWire } from "~/wire";

// What the backend names every organization at signup, so most rows carry it
// until someone renames.
const DEFAULT_ORGANIZATION_NAME = "My Organization";

/**
 * The organization the user is currently working inside, the organizations they
 * can move between, and every project they reach across all of them.
 *
 * Accessors are plain functions rather than memos: each returns a fresh array,
 * which a memo could not short-circuit on.
 */
export const useOrgScope = () => {
  const wire = useWire();
  const navigate = useNavigate();
  const routeParams = useParams();
  const { t } = useI18n();

  // Every organization the user can act in: one they hold a role in, or one
  // holding a project they reach through a grant on the project alone. The
  // second kind carries no org role, so it would otherwise be a scope the user
  // works inside and the sidebar never names. An organization with neither is
  // left out — switching into it has nowhere to land.
  const organizations = () =>
    wire.tenants
      .tenants()
      .filter(
        (tenant) =>
          tenant.organizationRole !== undefined ||
          wire.projects.all().some((project) => project.organizationId === tenant.organizationId)
      );

  const isMultiOrg = () => organizations().length > 1;

  // The URL wins where it names an org. Below that, the loaded project's own
  // org beats wire.currentOrgId(), which reads /context and so trails a switch
  // by a round-trip; and on /orgs/:orgId wire.currentProjectId() falls back to
  // the most recent project, which can belong to a different org entirely.
  const currentOrganizationId = () => {
    const orgId = routeParams.orgId;
    if (orgId) return orgId;
    const projectId = wire.currentProjectId();
    const project = projectId ? wire.projects.get(projectId as ProjectId) : undefined;
    return project?.organizationId ?? wire.currentOrgId();
  };

  const currentOrganization = () => organizations().find((tenant) => tenant.organizationId === currentOrganizationId());

  // The organization's own page serves members too, but settings and members
  // live on child routes the layout renders only for an owner.
  const ownedCurrentOrgId = () => {
    const current = currentOrganization();
    return current?.organizationRole === ROLE_OWNER ? current.organizationId : undefined;
  };

  const ownedOrganizations = () => organizations().filter((tenant) => tenant.organizationRole === ROLE_OWNER);

  // Working inside someone else's organization says nothing about whether the
  // user owns one of their own, so the links to billing and organization
  // settings fall back to an owned organization rather than disappearing.
  const defaultOwnedOrganization = () => ownedOrgTenant(organizations(), currentOrganizationId());

  const organizationName = (tenant: TenantSummary) =>
    tenant.organizationName || tenant.tenantName || t("organization.title");

  const organizationNameById = (organizationId: string) => {
    const tenant = organizations().find((candidate) => candidate.organizationId === organizationId);
    return tenant ? organizationName(tenant) : t("organization.title");
  };

  const hasDefaultName = (tenant: TenantSummary) => organizationName(tenant) === DEFAULT_ORGANIZATION_NAME;

  const organizationRoleLabel = (tenant: TenantSummary) => {
    switch (tenant.organizationRole) {
      case "role.owner":
        return t("organization.members.role_owner");
      case "role.collaborator":
        return t("organization.members.role_collaborator");
      case "role.reader":
        return t("organization.members.role_reader");
      // Reached through a project, not through membership. Naming any role here
      // would claim standing in the organization the user does not have.
      default:
        return t("organization.switcher.project_access");
    }
  };

  // Nearly every row would say owner, so the role only earns its width on the
  // roles that don't — above all the access that comes through a project alone.
  // The owner rides along where it names someone else: on an organization you
  // created it is your own address, which tells two rows still on the signup
  // default apart no better than their shared name does.
  const organizationRowDetail = (tenant: TenantSummary) => {
    const role = tenant.organizationRole === ROLE_OWNER ? undefined : organizationRoleLabel(tenant);
    const ownerEmail = tenant.organizationOwnerEmail;
    const isSelf = ownerEmail?.toLowerCase() === wire.auth?.user()?.primaryEmail?.toLowerCase();
    return [role, isSelf ? undefined : ownerEmail].filter(Boolean).join(" · ");
  };

  // Deliberately not narrowed to the focused organization: the surfaces reading
  // it cross that boundary on purpose.
  const reachableProjects = (): Project[] => wire.projects.all();

  const switchToOrganization = (organizationId: string) => {
    if (organizationId === currentOrganizationId()) return;
    const target = orgDefaultProject(wire.projects.all(), organizationId);
    // An organization with no reachable project has nowhere else to land.
    if (!target) {
      navigate(`/orgs/${organizationId}`);
      return;
    }
    wire.userContext.switchToProject(target.id);
  };

  return {
    organizations,
    isMultiOrg,
    currentOrganization,
    ownedCurrentOrgId,
    ownedOrganizations,
    defaultOwnedOrganization,
    organizationName,
    organizationNameById,
    hasDefaultName,
    organizationRoleLabel,
    organizationRowDetail,
    reachableProjects,
    switchToOrganization,
  };
};
